import { localDateToIsoDate } from '@/lib/date'
import type { QuoteDetail, QuoteHistory, QuoteStatus } from '@/types'

export type PortalQuoteStatus = 'draft' | 'sent' | 'won' | 'lost'
export type QuoteHistoryVisualTone =
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'neutral'

export type QuoteGroupFilter =
  | { status: PortalQuoteStatus }
  | {
      quote_group:
        | 'waiting'
        | 'expires_soon'
        | 'expired'
        | 'won'
        | 'signature_rate'
    }

export type QuoteNextStep =
  | { action: 'send'; tone: 'orange'; labelKey: 'toSend' }
  | {
      action: 'resend'
      tone: 'orange'
      labelKey: 'daysRemaining' | 'expired'
      daysRemaining: number
    }
  | {
      action: 'invoice'
      tone: 'green'
      labelKey: 'amountToCollect'
      amountToCollect: number
    }
  | { action: 'resend_adjusted'; tone: 'red'; labelKey: 'refused' }

export function portalQuoteStatus(status: QuoteStatus): PortalQuoteStatus {
  switch (status) {
    case 'draft':
      return 'draft'
    case 'sent':
      return 'sent'
    case 'won':
      return 'won'
    case 'lost':
      return 'lost'
    default:
      return 'draft'
  }
}

export function defaultQuoteValidUntil(createdAt: Date = new Date()): string {
  return localDateToIsoDate(addCalendarMonths(createdAt, 3))
}

export function quoteSentDate(
  quote: Pick<QuoteDetail, 'sent_at'>,
  historyItems: QuoteHistory[] = []
): string | null {
  return (
    quote.sent_at ??
    historyItems.find(
      item =>
        item.event_type === 'quote.sent' ||
        quoteHistoryTargetStatus(item) === 'sent'
    )?.timestamp ??
    null
  )
}

export function quoteTerminalStatusDate(
  quote: Pick<QuoteDetail, 'status' | 'accepted_at' | 'updated_at'>,
  historyItems: QuoteHistory[] = []
): string | null {
  const portalStatus = portalQuoteStatus(quote.status)
  if (portalStatus !== 'won' && portalStatus !== 'lost') return null

  if (portalStatus === 'won' && quote.accepted_at) return quote.accepted_at

  const terminalHistory = historyItems.find(item => {
    if (
      portalStatus === 'won' &&
      ['quote.accepted', 'quote.signed'].includes(item.event_type)
    ) {
      return true
    }

    if (portalStatus === 'lost' && item.event_type === 'quote.terminated') {
      return true
    }

    return quoteHistoryTargetStatus(item) === portalStatus
  })

  return terminalHistory?.timestamp ?? quote.updated_at ?? null
}

export function quoteHistoryVisualTone(
  item: Pick<QuoteHistory, 'event_type' | 'properties'>
): QuoteHistoryVisualTone {
  const targetStatus = quoteHistoryTargetStatus(item)

  if (targetStatus === 'won') return 'success'
  if (targetStatus === 'sent') return 'info'
  if (targetStatus === 'lost') return 'danger'
  if (targetStatus === 'draft') return 'warning'

  switch (item.event_type) {
    case 'quote.accepted':
    case 'quote.signed':
    case 'quote.converted_invoice':
    case 'quote.converted_contract':
      return 'success'
    case 'quote.sent':
      return 'info'
    case 'quote.deleted':
    case 'quote.terminated':
      return 'danger'
    case 'quote.created':
    case 'quote.updated':
    case 'quote.duplicated':
      return 'warning'
    default:
      return 'neutral'
  }
}

export function quoteGroupForPortalStatus(
  status: PortalQuoteStatus
): QuoteGroupFilter {
  return { status }
}

export function quoteGroupForKpi(
  group: 'waiting' | 'expires_soon' | 'expired' | 'won' | 'signature_rate'
): QuoteGroupFilter {
  return { quote_group: group }
}

export function quoteNextStep(
  quote: Pick<QuoteDetail, 'status' | 'total' | 'valid_until'>,
  today: string | Date = new Date()
): QuoteNextStep {
  const portalStatus = portalQuoteStatus(quote.status)

  if (portalStatus === 'sent') {
    const daysRemaining = daysUntil(quote.valid_until, today)

    return {
      action: 'resend',
      tone: 'orange',
      labelKey: daysRemaining < 0 ? 'expired' : 'daysRemaining',
      daysRemaining,
    }
  }

  if (portalStatus === 'won') {
    return {
      action: 'invoice',
      tone: 'green',
      labelKey: 'amountToCollect',
      amountToCollect: quote.total,
    }
  }

  if (portalStatus === 'lost') {
    return {
      action: 'resend_adjusted',
      tone: 'red',
      labelKey: 'refused',
    }
  }

  return {
    action: 'send',
    tone: 'orange',
    labelKey: 'toSend',
  }
}

export function quoteTitle(quote: { title?: string | null }): string {
  return quote.title?.trim() || '—'
}

export function quoteInterventionReference(quote: {
  job_id?: string | null
  job_reference?: string | null
}): string | null {
  return quote.job_reference?.trim() || quote.job_id || null
}

function daysUntil(target: string | null | undefined, today: string | Date) {
  if (!target) return 0

  const start = startOfDay(today)
  const end = startOfDay(target)

  return Math.ceil((end.getTime() - start.getTime()) / 86_400_000)
}

function startOfDay(value: string | Date) {
  const date =
    value instanceof Date ? new Date(value) : new Date(`${value}T00:00:00`)
  date.setHours(0, 0, 0, 0)

  return date
}

function addCalendarMonths(date: Date, months: number): Date {
  const result = new Date(date)
  const originalDay = result.getDate()

  result.setDate(1)
  result.setMonth(result.getMonth() + months)

  const lastDayOfTargetMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0
  ).getDate()

  result.setDate(Math.min(originalDay, lastDayOfTargetMonth))

  return result
}

function quoteHistoryTargetStatus(
  item: Pick<QuoteHistory, 'event_type' | 'properties'>
): PortalQuoteStatus | null {
  const properties = item.properties
  if (!isRecord(properties)) return null

  const direct = normalizePortalQuoteStatus(properties.status)
  if (direct) return direct

  const changes = properties.changes
  if (!isRecord(changes)) return null

  const statusChange = changes.status
  if (!isRecord(statusChange)) return null

  return normalizePortalQuoteStatus(
    statusChange.new ?? statusChange.to ?? statusChange.after
  )
}

function normalizePortalQuoteStatus(value: unknown): PortalQuoteStatus | null {
  if (typeof value !== 'string') return null

  switch (value.toLowerCase()) {
    case 'draft':
    case 'new':
    case 'suspended':
      return 'draft'
    case 'sent':
    case 'expired':
      return 'sent'
    case 'signed':
    case 'won':
    case 'converted':
      return 'won'
    case 'declined':
    case 'lost':
      return 'lost'
    default:
      return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
